/**
 * Drawer de criação: o painel lateral com os campos MÍNIMOS de uma entidade. É o
 * primeiro passo do padrão — cadastrar o essencial sem sair da lista. Ao salvar, o
 * componente navega para a edição em tela cheia do registro criado, onde o resto dos
 * campos e as correlações são geridos.
 *
 * O backend responde 201 (criou) ou 200 (dedup: já existia um de mesmo identificador
 * forte). Ambos trazem `id` e ambos levam à edição — a diferença é só o aviso.
 *
 * Quando o descriptor declara `importar` (ex.: veículo por CRLV), o cadastro oferece DUAS vias:
 * **importar** um documento (a IA lê os dados e correlaciona) OU **inserir** à mão. Na importação,
 * cria-se o registro mínimo, anexa-se o documento, extrai-se e o operador REVISA antes de gravar.
 */
import { Paperclip, ScanLine, Keyboard } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CampoDinamico, type ValorCampo } from '@/components/crud/CampoDinamico';
import { ModalRevisaoExtracao } from '@/components/crud/ModalRevisaoExtracao';
import { Sheet } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/misc';
import {
  type DocumentoCad,
  enviarArquivoCampos,
  type ExtracaoCad,
  extrairDocumento,
  listarTiposDocumento,
  type TipoDocumentoCad,
} from '@/lib/api';
import { criarRecurso } from '@/lib/cadastroApi';
import { corpoCriar, valoresIniciais, type Valores } from '@/lib/recursos/formulario';
import type { DescritorRecurso, RecursoBase } from '@/lib/recursos/tipos';

type Modo = 'importar' | 'inserir';

export function DrawerCriar({
  descritor,
  aberto,
  onAbrir,
}: {
  descritor: DescritorRecurso;
  aberto: boolean;
  onAbrir: (v: boolean) => void;
}) {
  const navegar = useNavigate();
  const [valores, setValores] = useState<Valores>(() => valoresIniciais(descritor.camposCriar));
  const [salvando, setSalvando] = useState(false);
  // Importação (só quando o descriptor a declara).
  const importavel = !!descritor.importar && !!descritor.ownerTipo;
  const [modo, setModo] = useState<Modo>(importavel ? 'importar' : 'inserir');
  const [arquivo, setArquivo] = useState<File | null>(null);
  // Seleção do tipo de documento (quando o owner tem vários: pessoa/empresa). Vazio = catálogo único.
  const selecionavel = !!descritor.importar?.selecionavel;
  const [tipos, setTipos] = useState<TipoDocumentoCad[]>([]);
  const [tipoSel, setTipoSel] = useState(descritor.importar?.tipoDocumento ?? '');
  // Revisão da extração após criar+anexar; ao aplicar/fechar, navega para a edição.
  const [revisao, setRevisao] = useState<{ extracao: ExtracaoCad; id: string } | null>(null);

  useEffect(() => {
    if (!importavel || !selecionavel || !descritor.ownerTipo) return;
    void listarTiposDocumento(descritor.ownerTipo).then((r) => {
      if (!r.ok) return;
      setTipos(r.dados);
      setTipoSel((atual) => atual || r.dados[0]?.id || '');
    });
  }, [importavel, selecionavel, descritor.ownerTipo]);

  const faltaObrigatorio = descritor.camposCriar.some(
    (c) => c.obrigatorio && !String(valores[c.chave] ?? '').trim(),
  );
  const usandoImport = importavel && modo === 'importar';
  // O tipo efetivo: o escolhido (pessoa/empresa) ou o fixo do descriptor (veículo/CRLV).
  const tipoDoc = selecionavel ? tipoSel : descritor.importar?.tipoDocumento ?? '';
  const podeSalvar = usandoImport ? !!arquivo && !!tipoDoc : !faltaObrigatorio;

  function ajustar(chave: string, v: ValorCampo) {
    setValores((atual) => ({ ...atual, [chave]: v }));
  }

  function irParaEdicao(id: string) {
    onAbrir(false);
    setValores(valoresIniciais(descritor.camposCriar));
    setArquivo(null);
    navegar(`/cadastro/${descritor.chave}/${id}`);
  }

  async function salvarManual() {
    const r = await criarRecurso<RecursoBase>(
      descritor.endpoint,
      corpoCriar(descritor.camposCriar, valores),
    );
    if (!r.ok) return void toast.error(r.erro);
    if (r.status === 200) {
      toast.info(`Este ${descritor.tituloSingular} já estava na base — abrindo o cadastro.`);
    } else {
      const t = descritor.tituloSingular;
      toast.success(`${t[0].toUpperCase()}${t.slice(1)} cadastrado.`);
    }
    irParaEdicao(r.dados.id);
  }

  async function salvarPorImportacao() {
    if (!arquivo || !descritor.importar || !descritor.ownerTipo || !tipoDoc) return;
    // 1) cria o registro mínimo (rascunho); 2) anexa o documento; 3) extrai; 4) abre a revisão.
    // `corpoMinimo` cobre campos obrigatórios (ex.: nome da pessoa) que a extração sobrescreve.
    const criado = await criarRecurso<RecursoBase>(
      descritor.endpoint,
      descritor.importar.corpoMinimo ?? {},
    );
    if (!criado.ok) return void toast.error(criado.erro);
    const id = criado.dados.id;
    const base = `/cadastro/${descritor.ownerTipo}/${id}/documentos`;
    const anexo = await enviarArquivoCampos<DocumentoCad>(base, arquivo, {
      categoria: tipoDoc,
      nome: arquivo.name,
    });
    if (!anexo.ok) {
      toast.error(anexo.erro);
      return irParaEdicao(id); // registro e talvez o anexo já existem; segue para a edição
    }
    const aviso = toast.loading('Lendo o documento e extraindo os dados…');
    const ext = await extrairDocumento(anexo.dados.id);
    toast.dismiss(aviso);
    if (!ext.ok) {
      toast.error(ext.erro);
      return irParaEdicao(id);
    }
    if (Object.keys(ext.dados.campos).length === 0) {
      toast('Nenhum dado legível foi encontrado — preencha os dados na edição.');
      return irParaEdicao(id);
    }
    // Abre a revisão; a navegação acontece ao aplicar ou fechar (o registro já existe).
    onAbrir(false);
    setRevisao({ extracao: ext.dados, id });
  }

  async function salvar(e: React.FormEvent) {
    e.preventDefault();
    if (!podeSalvar || salvando) return;
    setSalvando(true);
    try {
      if (usandoImport) await salvarPorImportacao();
      else await salvarManual();
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <Sheet
        aberto={aberto}
        onAbrir={onAbrir}
        titulo={`Novo(a) ${descritor.tituloSingular}`}
        descricao="Os campos essenciais. O resto — e as correlações — na tela seguinte."
        rodape={
          <>
            <Button type="button" variant="secundario" onClick={() => onAbrir(false)}>
              Cancelar
            </Button>
            <Button
              type="submit"
              form="form-criar"
              variant="primario"
              disabled={!podeSalvar || salvando}
            >
              {salvando ? <Spinner /> : usandoImport ? 'Importar e continuar' : 'Cadastrar'}
            </Button>
          </>
        }
      >
        {importavel && (
          <div className="mb-4 grid grid-cols-2 gap-2">
            <BotaoModo
              ativo={modo === 'importar'}
              onClick={() => setModo('importar')}
              icone={<ScanLine className="size-4" />}
              rotulo={descritor.importar!.rotulo}
            />
            <BotaoModo
              ativo={modo === 'inserir'}
              onClick={() => setModo('inserir')}
              icone={<Keyboard className="size-4" />}
              rotulo="Inserir manualmente"
            />
          </div>
        )}

        <form id="form-criar" onSubmit={salvar} className="flex flex-col gap-4">
          {usandoImport ? (
            <div className="flex flex-col gap-3">
              {descritor.importar!.descricao && (
                <p className="tipo-corpo-pequeno text-text-tertiary">
                  {descritor.importar!.descricao}
                </p>
              )}
              {selecionavel && (
                <label className="flex flex-col gap-2">
                  <span className="tipo-rotulo">Tipo de documento</span>
                  <select
                    value={tipoSel}
                    onChange={(e) => setTipoSel(e.target.value)}
                    className="h-10 rounded-md border border-border bg-input px-3 text-[15px] text-text outline-none focus-visible:border-border-strong focus-visible:shadow-[var(--focus-ring)]"
                  >
                    {tipos.length === 0 && <option value="">Carregando…</option>}
                    {tipos.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.rotulo}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="flex flex-col gap-2">
                <span className="tipo-rotulo">Documento</span>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="file"
                    className="hidden"
                    onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                  />
                  <span className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-[15px] text-text-secondary transition-colors hover:bg-surface-hover">
                    <Paperclip className="size-4" />
                    {arquivo ? 'Trocar arquivo' : 'Escolher arquivo'}
                  </span>
                  <span className="tipo-corpo-pequeno truncate text-text-tertiary">
                    {arquivo?.name ?? 'nenhum arquivo escolhido'}
                  </span>
                </label>
              </div>
            </div>
          ) : (
            descritor.camposCriar.map((campo) => (
              <CampoDinamico
                key={campo.chave}
                campo={campo}
                valor={valores[campo.chave] ?? ''}
                onChange={(v) => ajustar(campo.chave, v)}
                idPrefixo="criar"
              />
            ))
          )}
        </form>
      </Sheet>

      {importavel && (
        <ModalRevisaoExtracao
          extracao={revisao?.extracao ?? null}
          ownerTipo={descritor.ownerTipo!}
          ownerId={revisao?.id ?? ''}
          onFechar={() => {
            const id = revisao?.id;
            setRevisao(null);
            if (id) irParaEdicao(id);
          }}
          onAplicado={() => {
            /* a navegação ocorre no onFechar, que dispara em seguida */
          }}
        />
      )}
    </>
  );
}

function BotaoModo({
  ativo,
  onClick,
  icone,
  rotulo,
}: {
  ativo: boolean;
  onClick: () => void;
  icone: React.ReactNode;
  rotulo: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-[14px] transition-colors ' +
        (ativo
          ? 'border-primary bg-primary/10 text-text'
          : 'border-border text-text-secondary hover:bg-surface-hover')
      }
    >
      {icone}
      {rotulo}
    </button>
  );
}
